import { useState, useRef, useEffect } from 'react';
import { getIn } from 'formik';
import { uploadRMVendorItemImage, getImageSignedUrl } from '../Helpers/Api';

const FileUploader = ({
  formik,
  fieldName,
  label = 'Upload Image',
  isDisabled = false,
  acceptedFileTypes = 'image/jpeg,image/png,image/gif,image/webp',
  onSuccess = () => { },
}) => {
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [error, setError] = useState(null);
  const [previewUrls, setPreviewUrls] = useState({});
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fieldValue = getIn(formik.values, fieldName);

    if (formik && fieldValue) {
      const existingUrls = Array.isArray(fieldValue) ? fieldValue : [fieldValue];

      const existingFiles = existingUrls
        .filter((item) => item && typeof item === 'object' && item.url)
        .map((item, index) => ({
          name: `image-${index + 1}`,
          displayName: `Image ${index + 1}`,
          size: 0,
          type: 'image/jpeg',
          isExisting: true,
          url: item.url,
          data: item,
        }));

      setFiles(existingFiles);

      const existingProgress = {};
      const existingPreviews = {};

      const fetchSignedUrls = async () => {
        for (const file of existingFiles) {
          try {
            const { signedUrl } = await getImageSignedUrl(file.url);
            existingPreviews[file.name] = signedUrl;
            existingProgress[file.name] = 100;
          } catch (err) {
            console.error(`Failed to fetch signed URL for ${file.displayName}:`, err);
          }
        }
        setPreviewUrls((prev) => ({ ...prev, ...existingPreviews }));
        setUploadProgress((prev) => ({ ...prev, ...existingProgress }));
      };

      fetchSignedUrls();
    }
  }, [formik, fieldName]);

  const validateImageFile = (file) => {
    if (!file.type.startsWith('image/')) throw new Error('Please select a valid image file');
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) throw new Error('Image file size must be less than 10MB');
    return true;
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    const droppedFiles = Array.from(event.dataTransfer.files);
    handleFiles(droppedFiles);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleFiles = (newFiles) => {
    const validFiles = newFiles.filter((file) => {
      try {
        validateImageFile(file);
        return true;
      } catch (err) {
        console.error('File validation failed:', file.name, err.message);
        setError(err.message);
        return false;
      }
    });

    if (validFiles.length > 0) {
      // FIX: Don't spread the File object, just add the displayName property
      const filesWithDisplayNames = validFiles.map((file, index) => {
        // Create a new object that preserves the File object
        const fileWithDisplayName = file;
        fileWithDisplayName.displayName = `Image ${files.length + index + 1}`;
        return fileWithDisplayName;
      });

      setFiles((prevFiles) => [
        ...prevFiles,
        ...filesWithDisplayNames.filter((f) => !prevFiles.some((pf) => pf.name === f.name)),
      ]);
      uploadFiles(filesWithDisplayNames);
    }
  };

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    handleFiles(selectedFiles);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadFiles = async (filesToUpload) => {
    const uploadPromises = filesToUpload.map(async (file) => {
      try {
        setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));
        setError(null);

        const formData = new FormData();
        formData.append('image', file, file.name);
      
        const response = await uploadRMVendorItemImage(formData, (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            setUploadProgress((prev) => ({ ...prev, [file.name]: progress }));
          }
        });

        const imageData = response.data || response;
        const imageUrl = imageData?.url || imageData?.fileUrl;

        if (imageUrl) {
          const { signedUrl } = await getImageSignedUrl(imageUrl);
          setPreviewUrls((prev) => ({ ...prev, [file.name]: signedUrl }));
          setUploadProgress((prev) => ({ ...prev, [file.name]: 100 }));
          return { url: imageUrl }; // Normalize the response structure
        } else {
          throw new Error('Image URL not received from server');
        }
      } catch (err) {
        setError(`Failed to upload ${file.displayName}: ${err.message}`);
        setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));
        return null;
      }
    });

    const uploadedItems = (await Promise.all(uploadPromises)).filter(Boolean);
    if (uploadedItems.length > 0) {
      const existingUrls = getIn(formik.values, fieldName) || [];
      const allUrls = Array.isArray(existingUrls) ? existingUrls : [existingUrls];
      formik.setFieldValue(fieldName, [...allUrls, ...uploadedItems]);
      onSuccess({ fileUrls: uploadedItems });
    }
  };

  const handleRemoveFile = (fileName) => {
    const fileToRemove = files.find((f) => f.name === fileName);
    setFiles((prevFiles) => prevFiles.filter((f) => f.name !== fileName));

    setUploadProgress((prev) => {
      const newProgress = { ...prev };
      delete newProgress[fileName];
      return newProgress;
    });

    setPreviewUrls((prev) => {
      const newPreviews = { ...prev };
      delete newPreviews[fileName];
      return newPreviews;
    });

    const currentUrls = getIn(formik.values, fieldName) || [];
    const urlsArray = Array.isArray(currentUrls) ? currentUrls : [currentUrls];

    const updatedUrls = fileToRemove?.isExisting
      ? urlsArray.filter((item) => item.url !== fileToRemove.url)
      : urlsArray.filter((item) => !item.url.includes(fileName));

    formik.setFieldValue(fieldName, updatedUrls);
  };

  const handleImageClick = (file) => {
    if (previewUrls[file.name]) {
      setSelectedImage({
        url: previewUrls[file.name],
        name: file.displayName || `Image ${files.indexOf(file) + 1}`,
        originalUrl: file.url
      });
    }
  };

  const handleDownload = async () => {
    if (selectedImage) {
      try {
        const response = await fetch(selectedImage.url);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${selectedImage.name}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Download failed:', error);
      }
    }
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  return (
    <div className="container border rounded p-4 mb-4 bg-white">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <strong className="fs-6">{label}</strong>
        <small>{files.length} image{files.length !== 1 ? 's' : ''} selected</small>
      </div>

      {error && (
        <div className="alert alert-danger d-flex align-items-center" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          <div>{error}</div>
        </div>
      )}

      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !isDisabled && fileInputRef.current?.click()}
        className={`border border-${isDragOver ? 'primary' : 'secondary'} border-dashed rounded p-4 text-center ${isDisabled ? 'disabled' : 'cursor-pointer'}`}
        style={{ background: isDragOver ? '#e9f5ff' : '#f8f9fa' }}
      >
        <div className="mb-2">
          <i className="fas fa-cloud-upload-alt text-primary mb-2" style={{ fontSize: '24px' }}></i>
          <p className="mb-1">
            Drop your images here or <span className="text-primary">browse</span>
          </p>
          <small className="text-muted">Supports: JPEG, PNG, GIF, WebP (max 10MB)</small>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="d-none"
          accept={acceptedFileTypes}
          multiple
          disabled={isDisabled}
        />
      </div>

      {files.length > 0 && (
        <div className="mt-3">
          <h6>Images</h6>
          {files.map((file) => {
            const progress = uploadProgress[file.name] || 0;
            const isComplete = progress === 100;
            const isExisting = file.isExisting;
            const isUploading = progress > 0 && progress < 100;

            return (
              <div key={file.name} className="d-flex align-items-center p-3 border rounded mb-2 bg-light">
                <div className="me-3">
                  {previewUrls[file.name] ? (
                    <img
                      src={previewUrls[file.name]}
                      alt={file.displayName}
                      className="rounded cursor-pointer"
                      width={50}
                      height={50}
                      style={{ objectFit: 'cover' }}
                      onClick={() => handleImageClick(file)}
                    />
                  ) : (
                    <div
                      className="d-flex align-items-center justify-content-center bg-secondary rounded"
                      style={{ width: '50px', height: '50px' }}
                    >
                      <i className="fas fa-image text-white"></i>
                    </div>
                  )}
                </div>

                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong className="text-truncate">{file.displayName || `Image ${files.indexOf(file) + 1}`}</strong>
                    <div className="d-flex align-items-center">
                      {isComplete && !isExisting && (
                        <i className="fas fa-check-circle text-success me-2"></i>
                      )}
                      <button
                        type="button"
                        className="btn-close"
                        aria-label="Remove"
                        onClick={() => handleRemoveFile(file.name)}
                        disabled={isDisabled || isUploading}
                      />
                    </div>
                  </div>

                  {!isExisting && (
                    <>
                      <div className="d-flex justify-content-end small text-muted mb-1">
                        <span>{progress}%</span>
                      </div>
                      <div className="progress" style={{ height: '4px' }}>
                        <div
                          className={`progress-bar ${isComplete ? 'bg-success' : 'bg-primary'}`}
                          role="progressbar"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {formik?.touched?.[fieldName] && formik?.errors?.[fieldName] && (
        <div className="alert alert-danger mt-3">
          <i className="fas fa-exclamation-circle me-2"></i>
          {formik.errors[fieldName]}
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{selectedImage.name}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                ></button>
              </div>
              <div className="modal-body text-center p-4">
                <img
                  src={selectedImage.url}
                  alt={selectedImage.name}
                  className="img-fluid rounded"
                  style={{ maxHeight: '70vh', objectFit: 'contain' }}
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleDownload}
                >
                  <i className="fas fa-download me-2"></i>
                  Download
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { FileUploader };